import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { SelectMenu, Slideover, TypeaheadChips, useToast, useConfirm, PageHeader, DropdownMenu } from '../ui';
import ItemCoveragePanel, { resolveCoverageMode } from './quote-items/ItemCoveragePanel';
import RValuePicker from './quote-items/RValuePicker';
import PriceUploadSheet from './quote-items/PriceUploadSheet';
import { rSortKey } from './quote-items/coverage-widgets/RValueCoverage';
import { settingsAPI } from '../../services/api';
import { useDealerWorkspace } from '../admin/DealerWorkspaceContext';
import { PlusIcon, PencilSquareIcon, TrashIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import BulkActionBar, { BulkRowCheckbox } from '../ui/BulkActionBar';
import { PRODUCT_CATEGORIES, AREAS, titleCaseCategoryId, isPerSqftCategory } from '../../quote-builder/seedCatalog';
// IDI-208/IDI-136: buildCatalogRows/computeOverlayDelta moved to
// ../../quote-builder/catalogRows.js so QuoteBuilderPage (the live Quote
// Builder) can reuse the identical pricebook+overlay merge without pulling
// this whole Settings page component into that bundle. Re-exported below so
// existing imports (QuoteItemsPage.DEF-162.test.js) keep working unchanged.
import { buildCatalogRows, computeOverlayDelta, mergeOverlayRows } from '../../quote-builder/catalogRows';
import { mergeAreasWithTenant, relevantBuiltInAreas } from '../../quote-builder/areasModel';
import { VOCAB_SEEDS } from '../../constants/vocabSeeds';
import useTableSort from '../ui/useTableSort';
import { SortIndicator } from '../ui/Table';
import { compareText } from '../../utils/tableSort';

// IDI-508 (D3): the page's fixed default order — Item Name, then Part #, then
// SKU — so reloads never reorder rows (listPricebook returns Go map order).
const byNamePartSku = (a, b) =>
  compareText(String(a.name || ''), String(b.name || ''))
  || compareText(String(a.partNumber || ''), String(b.partNumber || ''))
  || compareText(String(a.sku || ''), String(b.sku || ''));

// IDI-508 (D1): what each cell shows, shared by the cell and its sort.
const itemTypeLabel = (i) => (i.categoryId
  ? (PRODUCT_CATEGORIES.find((c) => c.id === i.categoryId)?.label || titleCaseCategoryId(i.categoryId))
  : '–');
const rValueLabel = (i) => (i.itemType === 'accessory' ? '–' : (i.rValue || '–'));
// IDI-611 (D2): QTY on Hand as saved (1.62, -3, 0); '—' when this dealer hasn't set it.
const qtyLabel = (i) => (i.qtyOnHand === '' || i.qtyOnHand == null ? '—' : String(i.qtyOnHand));
const statusLabel = (i) => ((i.status || 'active') === 'active' ? 'Active' : 'Inactive');

// IDI-508 (D1): sortable columns, by each cell's shown value. Cost sorts by
// cents; $0 / blank show '—' and so sort last both ways.
const CATALOG_SORT_COLUMNS = [
  { key: 'name', type: 'text', value: (i) => i.name },
  { key: 'itemType', type: 'text', value: itemTypeLabel },
  { key: 'rValue', type: 'text', value: rValueLabel },
  { key: 'cost', type: 'money', value: (i) => (i.itemCostPerUnit ? Number(i.itemCostPerUnit) : '—') },
  { key: 'status', type: 'text', value: statusLabel },
  { key: 'partNumber', type: 'text', value: (i) => i.partNumber || '—' },
  { key: 'qtyOnHand', type: 'number', value: qtyLabel },
];
const SORT_ARIA = { asc: 'ascending', desc: 'descending' };

// Wraps the page's own <th> (its text and styling unchanged) and puts the
// shared sort button + indicator inside it.
function SortableTh({ children: th, sortKey, sortDirection, onSort }) {
  const active = sortDirection === 'asc' || sortDirection === 'desc' ? sortDirection : null;
  const right = th.props.style?.textAlign === 'right';
  return React.cloneElement(th, {
    'aria-sort': SORT_ARIA[active] || 'none',
    'data-sort-key': sortKey,
    children: (
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 cursor-pointer select-none"
        style={{
          background: 'transparent', border: 0, padding: 0, color: 'inherit', font: 'inherit',
          letterSpacing: 'inherit', textTransform: 'inherit',
          flexDirection: right ? 'row-reverse' : undefined,
        }}
      >
        {th.props.children}
        <SortIndicator direction={active} />
      </button>
    ),
  });
}

// PR-02a §1a.1: accessory unit controlled list. No free text.
// Consumed by the accessory picker in PR-02b as the snapshotted line-item unit.
const ACCESSORY_UNITS = [
  { value: 'box', label: 'box' },
  { value: 'roll', label: 'roll' },
  { value: 'can', label: 'can' },
  { value: 'bag', label: 'bag' },
  { value: 'each', label: 'each' },
];

// IDI-505 D4: an accessory's Areas are the dealer's areas as Add Item lists
// them (renamed, added; no inactive). The stored value is the area id; a
// stored id no longer offered still shows (seed label, else the id).
// Empty selection = available everywhere.
const areaOptionsFor = (subAreas, customAreas, stored = []) => {
  const merged = mergeAreasWithTenant(
    relevantBuiltInAreas(VOCAB_SEEDS.area.values, subAreas, customAreas), subAreas, customAreas);
  const opts = merged.map((a) => ({ value: a.id, label: a.label }));
  const missing = (stored || []).filter((id) => !opts.some((o) => o.value === id))
    .map((id) => ({ value: id, label: AREAS.find((a) => a.id === id)?.label || String(id) }));
  return [...opts, ...missing];
};

// §5.2 Quote item types (closed vocabulary; drives coverage-chart shape per §5.3)
// DEF-163: canonical form for money on catalog items is integer CENTS.
// The user types DOLLARS (e.g. "10.00" or "10"); we store cents (1000).
// A dollar-formatted string is shown in the input; storage is always the integer.
// DEF-163: exported for tests.
export const inputDollarsToCents = (str) => {
  if (str === '' || str == null) return '';
  // Accept "10", "10.00", "10.5". Round to nearest cent (integer).
  const dollars = Number(str);
  if (!Number.isFinite(dollars)) return '';
  return Math.round(dollars * 100);
};
export const centsToInputDollars = (cents) => {
  if (cents === '' || cents == null) return '';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  // Show "1.79" for 179, "10" for 1000 (drop trailing zeros for keystroke friendliness).
  return (n / 100).toString();
};

// IDI-428: the per-item "Item Type" picker is gone (Sarah: "Item type is
// still lingering"); the category decides the product's fields.


const categoryToItemType = (categoryId) => {
  if (/spray_foam/.test(categoryId)) return 'spray_foam';
  if (/blow/.test(categoryId)) return 'blown_insulation';
  if (categoryId === 'accessories' || categoryId === 'light_covers') return 'accessory';
  return 'blown_insulation';
};

export { buildCatalogRows, computeOverlayDelta };

/**
 * Validate a bulk-change selection before applying.
 * Returns { ok: true, targets } or { ok: false, error }.
 *
 * IDI-94: refuse to act when nothing is selected — the old fallback
 * (apply to every active item in the current filter) created a false
 * sense of safety when checkboxes were visible but unchecked.
 */
export function validateBulkSelection(selectedIds, items) {
  if (!selectedIds || selectedIds.length === 0) {
    return { ok: false, error: 'Select at least one item first' };
  }
  const targets = items.filter(
    i => selectedIds.includes(i.id) && (i.status || 'active') === 'active'
  );
  if (targets.length === 0) {
    return { ok: false, error: 'No active items in selection' };
  }
  return { ok: true, targets };
}

/**
 * IDI-509: a Bulk Change's per-row result. Cost (cents): 'percent', 'dollar'
 * (dollars per unit) or 'sqft' (set cost per sq ft: batts set with Sq ft per
 * unit over 1 -> per-unit cost; AeroBarrier/Removal as stored; others stay).
 * An unset cost, no change, or a result of $0 or below stays (D3, D4). QTY:
 * 'add' or 'set', blank counts as 0 (D5). Returns { changes: [{ row, after }], stays }.
 */
export function bulkChangeRows(rows, field, mode, value) {
  const changes = [];
  for (const row of rows) {
    let after;
    if (field === 'qtyOnHand') {
      after = mode === 'set' ? value : (Number(row.qtyOnHand) || 0) + value;
      if (after === row.qtyOnHand) after = undefined;
    } else {
      const cost = Number(row.itemCostPerUnit) || 0;
      const kind = productKindFor(row);
      const sqft = Number(row.squareFeetPerUnit) || 0;
      if (mode === 'percent') after = Math.round(cost * (1 + value / 100));
      else if (mode === 'dollar') after = cost + Math.round(value * 100);
      else if (kind === 'persqft') after = Math.round(value * 100);
      else if (kind === 'batts' && sqft > 1) after = Math.round(value * 100 * sqft);
      if (!(cost > 0 && after > 0 && after !== cost)) after = undefined;
    }
    if (after !== undefined) changes.push({ row, after });
  }
  return { changes, stays: rows.length - changes.length };
}

// IDI-9: flat catalog pagination page size. 50 keeps the DOM light while
// giving a dense enough page for the Friday walk.
const PAGE_SIZE = 50;

// IDI-506 D2: batt material needs an R-value on a New Item; boards and
// Safing don't (Sarah 10-14: "not required").
const RVALUE_REQUIRED_CATEGORY_IDS = new Set([
  'kraft_batts', 'unfaced_batts', 'fsk_batts', 'foil_batts',
  'mineral_wool', 'safb_mineral_wool', 'mbi_rolls',
]);

/**
 * Iter18 R2 §1.5.1 (E.5): NOTE — the coverage-seed data that used to live
 * here (COVERAGE_SEED) moved to quote-builder/catalogRows.js (IDI-208/
 * IDI-136), then was DELETED entirely there (IDI-208, 2026-09-01 ruling) --
 * confirmed dead code, 0 of 11,180 real pricebook rows ever matched its 4
 * fake seed-catalog part numbers. The schema it demonstrated is preserved
 * at quote-builder/coverageShapeFixture.js as a reference (not consumed by
 * any runtime path). Real per-item coverage data exists unmigrated in the
 * legacy source (ItemUsages.csv, 65,884 rows) -- see IDI-221.
 */

// DEF-162: default categoryId per itemType. If the user doesn't pick one,
// the item still lands under a real heading rather than vanishing.
const DEFAULT_CATEGORY_FOR_TYPE = {
  spray_foam: 'cc_spray_foam',
  blown_insulation: 'fg_blow',
  accessory: 'accessories',
};

const blankItem = () => {
  // IDI-507 (acceptance 4): a net-new item needs a sku. Overlays are keyed by
  // sku (PR-33) and buildCatalogRows drops an overlay row without one, so a
  // New Item (and its vendor) vanished on reload. Its id is unique and stable.
  const id = `item-${Date.now()}`;
  return { ...blankItemFields(), id, sku: id };
};

const blankItemFields = () => ({
  name: '',
  salesDescription: '',
  itemType: 'blown_insulation',
  // DEF-162 §5a: derive categoryId from itemType via the canonical map —
  // never hardcode a literal, which silently defeats the Uncategorised safety net.
  categoryId: DEFAULT_CATEGORY_FOR_TYPE['blown_insulation'],
  // R20-19: filterable status. Defaults to active on new items.
  status: 'active',
  // PR-02a: accessory-only fields. Ignored for non-accessory item types.
  unit: '',
  areaScope: [],
  // IDI-506 D3: never pre-filled.
  rValue: '',
  squareFeetPerUnit: '',
  itemCostPerUnit: '',
  partNumber: '',
  qtyOnHand: '',
  defaultVendor: '',
  defaultWarehouse: '',
});

/**
 * Iter18 R2 §2.5: Item editor now uses the shared Slideover primitive so the
 * scrim + panel are portaled to <body> — fixes the stray top-margin bug where
 * the Settings shell's overflow container was clipping the bespoke fixed
 * elements.
 *
 * Iter18 R2 §1.5.1 (E.2): Coverage lives inside the item editor now via
 * <ItemCoveragePanel/>. The legacy standalone /settings/coverage-charts page
 * and CoverageChartModal wrapper were retired — hard-delete per Q8.
 */
// IDI-428 (09-29 call, Sarah + Lindsey): the side sheet's fields depend on
// the product type. Derived from the item's category (IDI-135 moves the mode
// selector up to the category; "Item Type" is no longer shown on any item).
//   blowin    : no R-Value, no Sq ft per unit, "Item Cost Per Bag", attic/wall chart
//   sprayfoam : no R-Value, no Sq ft per unit, "Item Cost Per Set", foam chart
//   batts     : R-Value, Sq ft per unit, Cost Per Unit AND Per Sq Ft (linked), NO chart
//   accessory : unit + Cost Per Unit (unchanged)
//   other     : the prior generic fields (boards, safing, etc. are not yet specified)
// IDI-338: what the Item Catalog sends to action=pricebook_override for one
// item, by product type. Only the fields the type uses are sent, so saving a
// batt never writes a blown-in chart and vice versa. itemCostPerUnit is
// already integer cents here (handleFormSave converts).
// IDI-507 (1)(2)(6): `original` is the row as loaded (what the sheet opened
// with). Coverage is sent ONLY when the user changed it, so saving the cost of
// an item that shows IDI's central chart no longer copies that chart into the
// dealer's override (which cut the dealer off from IDI's later updates). A
// changed coverage is sent with clear_coverage, which REPLACES the dealer's
// coverage: a chart blanked to nothing is removed, and all-blank means "back
// to IDI's value". Status is mirrored as is_active. Without `original` (a new
// row) any coverage present is the user's own and is sent.
const positiveChart = (m) => {
  const out = {};
  Object.entries(m || {}).forEach(([k, v]) => { const n = Number(v); if (v !== null && v !== '' && n > 0) out[k] = n; });
  return out;
};
const coverageFingerprint = (item, kind) => {
  if (kind === 'blowin') return JSON.stringify([positiveChart(item.coverage_rvalues_attic), positiveChart(item.coverage_rvalues_wall)]);
  if (kind === 'sprayfoam') return JSON.stringify([Number(item.coverage_per_set) || 0, Number(item.coverage_max_thickness_in) || 0]);
  return JSON.stringify(Number(item.squareFeetPerUnit) > 1 ? Number(item.squareFeetPerUnit) : 0);
};
export function coverageChanged(item, original) {
  if (!original) return true;
  const kind = productKindFor(item);
  const k = kind === 'blowin' || kind === 'sprayfoam' ? kind : 'other';
  return coverageFingerprint(item, k) !== coverageFingerprint(original, k);
}

export function pricebookOverrideBody(item, original = null) {
  const kind = productKindFor(item);
  const body = { sku: String(item.sku || item.partNumber) };
  const cents = Number(item.itemCostPerUnit);
  if (Number.isFinite(cents) && cents >= 0 && item.itemCostPerUnit !== '' && item.itemCostPerUnit != null) body.cost_cents = Math.round(cents);
  body.is_active = (item.status || 'active') === 'active';
  // IDI-499 D2 (F1): AeroBarrier / Removal are cost-only.
  if (kind === 'persqft') return body;
  if (!coverageChanged(item, original)) return body;
  // A changed coverage REPLACES the dealer's (IDI-507 (2)); without an
  // original there's nothing to replace, so a new row only adds.
  if (original) body.clear_coverage = true;
  if (kind === 'blowin') {
    const attic = positiveChart(item.coverage_rvalues_attic);
    const wall = positiveChart(item.coverage_rvalues_wall);
    if (Object.keys(attic).length || Object.keys(wall).length) body.coverage_mode = 'rvalue-attic-wall';
    if (Object.keys(attic).length) body.coverage_rvalues_attic = attic;
    if (Object.keys(wall).length) body.coverage_rvalues_wall = wall;
  } else if (kind === 'sprayfoam') {
    if (Number(item.coverage_per_set) > 0) {
      body.coverage_mode = 'sprayfoam';
      body.coverage_per_set = Number(item.coverage_per_set);
      if (Number(item.coverage_max_thickness_in) > 0) body.coverage_max_thickness_in = Number(item.coverage_max_thickness_in);
    }
  } else if (Number(item.squareFeetPerUnit) > 1) {
    body.coverage_mode = 'sqft';
    body.coverage_sqft_per_unit = Number(item.squareFeetPerUnit);
  }
  return body;
}

// IDI-507 (8): "what failed" + the server's reason (api.js puts a friendly
// message on err.message).
export function saveErrorMessage(what, err) {
  const why = err && typeof err.message === 'string' ? err.message.trim() : '';
  return why ? `${what}: ${why}` : `${what}.`;
}

// IDI-507 (5): migrated vendor / warehouse rows -> select options keyed by id
// (the pricebook stores vendor_id / warehouse_id). Inactive rows are dropped.
export function lookupOptions(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => r && r.id != null && r.active !== false && r.isActive !== false)
    .map((r) => ({ value: String(r.id), label: String(r.name || r.business_name || r.id) }))
    .filter((o) => o.label);
}

// IDI-499 D1: the rest of the batt material, boards and safing get the batts set, no chart.
const BATTS_SET_CATEGORY_IDS = new Set([
  'mineral_wool', 'safb_mineral_wool', 'mbi_rolls', 'xps_foam_board', 'polyiso_rigid', 'hd_fg_board', 'safing',
]);

// IDI-499 D1: fields follow categoryId; the hidden itemType only when there is none.
export function productKindFor(item) {
  const cat = item?.categoryId || '';
  if (cat === 'accessories' || cat === 'light_covers' || (!cat && item?.itemType === 'accessory')) return 'accessory';
  if (isPerSqftCategory(cat)) return 'persqft';
  if (BATTS_SET_CATEGORY_IDS.has(cat)) return 'batts';
  const mode = resolveCoverageMode(cat, cat ? null : item?.itemType);
  if (mode === 'rvalue-attic-wall') return 'blowin';
  if (mode === 'sprayfoam') return 'sprayfoam';
  if (mode === 'rvalue') return 'batts';
  return 'other';
}

// Batts: the two cost fields are linked through sq ft per unit (Sarah: "It'll
// calculate the bag price based off of the square feet per unit ... or vice
// versa"). Dollars in, dollars out; null when it can't be derived.
export function linkedBattCost({ perUnit, perSqft, sqftPerUnit }) {
  const cov = Number(sqftPerUnit);
  if (!(cov > 0)) return { perUnit: perUnit ?? '', perSqft: perSqft ?? '' };
  if (perSqft !== undefined && perSqft !== '' && perSqft !== null) {
    const u = Number(perSqft) * cov;
    return { perUnit: Number.isFinite(u) ? (Math.round(u * 100) / 100).toFixed(2) : '', perSqft };
  }
  if (perUnit !== undefined && perUnit !== '' && perUnit !== null) {
    const f = Number(perUnit) / cov;
    return { perUnit, perSqft: Number.isFinite(f) ? (Math.round(f * 1000) / 1000).toString() : '' };
  }
  return { perUnit: '', perSqft: '' };
}

// A stored value that isn't in the list (an inactive vendor, or a legacy
// free-text name) still shows, rather than reading as blank.
const withCurrent = (opts, current) =>
  current && !opts.some((o) => o.value === String(current))
    ? [...opts, { value: String(current), label: String(current) }]
    : opts;

function ItemEditorSheet({ item, isNew = false, isIdiItem = false, areaRows = { subAreas: [], customAreas: [] }, vendors, warehouses, open, onClose, onSave }) {
  // DEF-163: convert stored cents → dollar string for the input.
  // The form holds dollars while editing; conversion back to cents happens
  // in handleFormSave below. This keeps the input predictable (no fixed-point
  // rounding while typing "10.5") and localizes the money-units seam.
  const itemToForm = (i) => ({ ...i, itemCostPerUnit: centsToInputDollars(i.itemCostPerUnit) });
  const [form, setForm] = useState(() => itemToForm(item));
  // IDI-441 (S3, D3): the dealer pressed "Reset to IDI default" in this sheet.
  const [resetToIdi, setResetToIdi] = useState(false);

  // Reset form state whenever the operator opens a different item.
  useEffect(() => { if (open) { setForm(itemToForm(item)); setResetToIdi(false); } }, [open, item]);

  // IDI-441 (S3, D3 ruled 2026-10-14): drop the dealer's own coverage so IDI's
  // central chart applies. Only the coverage is emptied (price isn't reset,
  // IDI-43); Save Item then sends clear_coverage with no coverage
  // (pricebookOverrideBody, via coverageChanged).
  const resetCoverageToIdi = () => {
    setForm((f) => ({
      ...f,
      coverage_rvalues_attic: {},
      coverage_rvalues_wall: {},
      coverage_per_set: '',
      coverage_max_thickness_in: '',
    }));
    setResetToIdi(true);
  };

  // DEF-163: convert dollar string → integer cents before persisting.
  // §3: also normalize rValue to null for accessories (BP-7: normalize on write
  // even for existing records — the record is what the next reader trusts).
  const handleFormSave = () => {
    const isAcc = productKindFor(form) === 'accessory';
    const { itemCostPerSqft: _derivedOnly, ...rest } = form;
    // IDI-506 D3: none saves as '' (never '–'); a New Item of a type with
    // no R-Value field saves none. An existing one keeps what it has.
    const saveKind = productKindFor(form);
    let rValue = form.rValue === '–' ? '' : form.rValue;
    if (isNew && saveKind !== 'batts') rValue = '';
    const payload = {
      ...rest,
      itemCostPerUnit: inputDollarsToCents(form.itemCostPerUnit),
      rValue: isAcc ? null : rValue,
    };
    onSave(payload);
  };

  // DEF-162: when itemType changes, auto-suggest a matching categoryId so the
  // item lands under a real catalog heading. The user can still override the
  // Category picker below to move it to a different group.
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === 'itemType' && DEFAULT_CATEGORY_FOR_TYPE[v]) {
      // Only auto-suggest when the current categoryId doesn't already match the
      // new itemType's canonical set (avoids overriding an operator's explicit pick).
      const currentMatchesNewType = categoryToItemType(f.categoryId) === v;
      if (!currentMatchesNewType) next.categoryId = DEFAULT_CATEGORY_FOR_TYPE[v];
      // DEF-163 §4: accessories don't have R-values (the display renders '–').
      // Clear a stale rValue when switching TO accessory to keep the record clean.
      // §3 (this round): clear stored rValue to null on accessory. The UI already
      // suppresses display for accessories, but a stored '–' or 'R-19' is a
      // trap for any later reader that trusts the payload over the page.
      if (v === 'accessory') next.rValue = null;
    }
    return next;
  });
  // IDI-505 D2: a New Item picks its Item Type first; no type fields until then.
  const kind = isNew && !form.categoryId ? 'none' : productKindFor(form);
  const isAccessory = kind === 'accessory';
  // PR-02a §1a.1: accessories require `unit` + `itemCostPerUnit` (unit_cost snapshot); `squareFeetPerUnit`
  // is meaningless for accessories (no coverage — Q1 dropped coverage entirely).
  // Non-accessory items retain their existing validation contract.
  // DEF-162 §5a: categoryId MUST be a real value — the catalog groups by it,
  // and an absent value silently drops the item from the display.
  const knownCategoryIds = new Set(PRODUCT_CATEGORIES.map((c) => c.id));
  const validCategory = form.categoryId && knownCategoryIds.has(form.categoryId);
  // C1: sqft and cost are OPTIONAL. Cost arrives later as a per-dealer
  // negotiated-cost overlay; sqft is not something BIDIT captures today.
  // Required: name + itemType + validCategory (+ unit for accessories).
  // PR-31 (307_ §4b): on EDIT of an existing row (id present), relax
  // itemType + category. 96.7% of the pricebook has no itemType, and
  // dealers correcting a price should not have to invent a taxonomy value.
  // On NEW items the full validation still applies.
  // IDI-505: keyed on isNew — a blank item has an id too, so the id never
  // told a New Item from an existing one.
  const requiredNameOK = Boolean(form.name.trim());
  const requiredTypeOK = !isNew || Boolean(form.itemType);
  const requiredCategoryOK = !isNew || validCategory;
  const requiredUnitOK = isAccessory ? Boolean(form.unit) : true;
  // IDI-506 D2: a New batt-material item needs an R-value; an existing one
  // without one still saves.
  const rValueRequired = kind === 'batts' && RVALUE_REQUIRED_CATEGORY_IDS.has(form.categoryId);
  // IDI-683 D1: a set value (the one the sheet opened with) can't be removed.
  const openedWithRValue = Boolean(item && item.rValue && item.rValue !== '–');
  const requiredRValueOK = !((isNew || openedWithRValue) && rValueRequired) || Boolean(form.rValue && form.rValue !== '–');
  const valid = requiredNameOK && requiredTypeOK && requiredCategoryOK && requiredUnitOK && requiredRValueOK;

  // PR-31 (307_ §4a): explain WHY Save is disabled. Matches the Send Invoice
  // treatment shipped in PR-26 — a disabled control states its condition.
  const disabledReason = (() => {
    if (valid) return null;
    const missing = [];
    if (!requiredNameOK) missing.push('Name');
    if (!requiredTypeOK) missing.push('Item Type');
    if (!requiredCategoryOK) missing.push('Item Type');
    if (!requiredUnitOK) missing.push('Unit');
    if (!requiredRValueOK) missing.push('R-Value');
    return `Required: ${[...new Set(missing)].join(', ')}`;
  })();

  return (
    <>
      <Slideover
        open={open}
        onClose={onClose}
        title={item.name ? 'Edit Item' : 'New Item'}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleFormSave} disabled={!valid} title={disabledReason || undefined}>Save Item</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Name *" value={form.name} onChange={(e) => set('name', e.target.value)} />

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
              Sales Description
            </label>
            <textarea
              value={form.salesDescription}
              onChange={(e) => set('salesDescription', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm"
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            />
          </div>

          {/* IDI-428: "Item Type" is gone from every item; the category decides
              which fields show (productKindFor). */}
          <div className={kind === 'batts' ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 gap-3'}>
            <div>
              {/* DEF-162: category picker — the field the catalog groups by.
                  Without this, custom items land with no categoryId and vanish. */}
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>Item Type *</label>
              <SelectMenu
                width="full"
                value={form.categoryId || (isNew ? '' : DEFAULT_CATEGORY_FOR_TYPE[form.itemType] || 'accessories')}
                onChange={(val) => set('categoryId', val)}
                placeholder="Select item type…"
                options={PRODUCT_CATEGORIES.map((c) => ({ value: c.id, label: c.label }))}
              />
            </div>
            {kind === 'batts' && (
              <div>
                {/* Batts only: "R-value is tied to the specific item." */}
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
                  {rValueRequired ? 'R-Value *' : 'R-Value'}
                </label>
                <RValuePicker
                  label="R-Value"
                  value={form.rValue}
                  onChange={(val) => set('rValue', val)}
                  optional={!rValueRequired}
                />
              </div>
            )}
          </div>

          {kind === 'accessory' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>Unit *</label>
                <SelectMenu
                  width="full"
                  value={form.unit}
                  onChange={(val) => set('unit', val)}
                  placeholder="Select unit…"
                  options={ACCESSORY_UNITS}
                />
              </div>
              <Input label="Item Cost Per Unit" type="number" value={form.itemCostPerUnit} onChange={(e) => set('itemCostPerUnit', e.target.value)} />
            </div>
          )}
          {kind === 'blowin' && (
            <div className="grid grid-cols-1 gap-3">
              <Input label="Item Cost Per Bag" type="number" value={form.itemCostPerUnit} onChange={(e) => set('itemCostPerUnit', e.target.value)} />
            </div>
          )}
          {kind === 'sprayfoam' && (
            <div className="grid grid-cols-1 gap-3">
              <Input label="Item Cost Per Set" type="number" value={form.itemCostPerUnit} onChange={(e) => set('itemCostPerUnit', e.target.value)} />
            </div>
          )}
          {kind === 'batts' && (
            <div className="grid grid-cols-3 gap-3">
              <Input
                label="Square Feet Per Unit"
                type="number"
                value={form.squareFeetPerUnit}
                onChange={(e) => setForm((f) => ({ ...f, squareFeetPerUnit: e.target.value, itemCostPerSqft: linkedBattCost({ perUnit: f.itemCostPerUnit, sqftPerUnit: e.target.value }).perSqft }))}
              />
              <Input
                label={form.categoryId === 'mbi_rolls' ? 'Item Cost Per Roll' : 'Item Cost Per Unit'}
                type="number"
                value={form.itemCostPerUnit}
                onChange={(e) => setForm((f) => ({ ...f, itemCostPerUnit: e.target.value, itemCostPerSqft: linkedBattCost({ perUnit: e.target.value, sqftPerUnit: f.squareFeetPerUnit }).perSqft }))}
              />
              <Input
                label="Item Cost Per Square Foot"
                type="number"
                value={form.itemCostPerSqft ?? linkedBattCost({ perUnit: form.itemCostPerUnit, sqftPerUnit: form.squareFeetPerUnit }).perSqft}
                onChange={(e) => setForm((f) => ({ ...f, itemCostPerSqft: e.target.value, itemCostPerUnit: linkedBattCost({ perSqft: e.target.value, sqftPerUnit: f.squareFeetPerUnit }).perUnit }))}
              />
            </div>
          )}
          {kind === 'persqft' && (
            <div className="grid grid-cols-2 gap-3">
              {/* IDI-499 D2: cost per sq ft only, stored as cost_cents (cents per sq ft). */}
              <Input label="Item Cost Per Square Foot" type="number" value={form.itemCostPerUnit} onChange={(e) => set('itemCostPerUnit', e.target.value)} />
            </div>
          )}
          {kind === 'other' && (
            <div className="grid grid-cols-2 gap-3">
              <Input label="Square Feet Per Unit" type="number" value={form.squareFeetPerUnit} onChange={(e) => set('squareFeetPerUnit', e.target.value)} />
              <Input label="Item Cost Per Unit" type="number" value={form.itemCostPerUnit} onChange={(e) => set('itemCostPerUnit', e.target.value)} />
            </div>
          )}

          {/* PR-02a §1a.1: `areaScope` — optional multi-select of area ids. Empty = available everywhere. */}
          {isAccessory && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>Areas</label>
              <TypeaheadChips
                value={form.areaScope || []}
                onChange={(scope) => set('areaScope', scope)}
                options={areaOptionsFor(areaRows.subAreas, areaRows.customAreas, form.areaScope)}
                placeholder="Available in all areas"
                aria-label="Areas"
              />
            </div>
          )}

          {/* IDI-505 D5: Part # is editable on the dealer's own items; IDI's stays the catalog key. */}
          {!isIdiItem ? (
            <Input label="Part #" value={form.partNumber || ''} onChange={(e) => set('partNumber', e.target.value)} />
          ) : (
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
              Part # <span className="ml-1 text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>(catalog key — read-only)</span>
            </label>
            <input
              type="text"
              value={form.partNumber}
              readOnly
              title="Part # is the catalog key and cannot be edited."
              className="w-full px-3 py-2 text-sm cursor-not-allowed"
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-sunken)',
                color: 'var(--color-text-muted)',
              }}
            />
          </div>
          )}

          {/* DEF-162 §2a: qtyOnHand is numeric; use '' for absent, Number for present.
              One empty representation across the row model, the wire, and the display. */}
          <Input label="QTY on Hand" type="number" value={form.qtyOnHand} onChange={(e) => {
            const v = e.target.value;
            set('qtyOnHand', v === '' ? '' : Number(v));
          }} />

          <div>
            {/* R20-19: Status field for filtering. Defaults to active on new items. */}
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>Status</label>
            <SelectMenu
              width="full"
              value={form.status || 'active'}
              onChange={(val) => set('status', val)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>Default Vendor</label>
              <SelectMenu
                width="full"
                value={form.defaultVendor}
                onChange={(val) => set('defaultVendor', val)}
                placeholder="—"
                options={withCurrent(vendors, form.defaultVendor)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>Default Warehouse</label>
              <SelectMenu
                width="full"
                value={form.defaultWarehouse}
                onChange={(val) => set('defaultWarehouse', val)}
                placeholder="—"
                options={withCurrent(warehouses, form.defaultWarehouse)}
              />
            </div>
          </div>

          {/*
            Iter18 R2 §1.5.1: embedded coverage widget. Dispatches on the item's
            categoryId (spray-foam → auto-calc grid; blow → attic/wall dual
            grids; batts → single manual R-value grid; others → hidden).
          */}
          {/* IDI-428: batts have NO coverage chart (Sarah, Adrian agreeing). */}
          {(kind === 'blowin' || kind === 'sprayfoam') && (
            <>
              {form.coverageSource === 'central' && (
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }} data-testid="coverage-source-note">
                  Showing IDI's default coverage. Saving your own values applies them to your account only.
                </p>
              )}
              {form.coverageSource === 'dealer' && form.coverageHasIdiDefault === true && (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }} data-testid="coverage-source-note">
                    {resetToIdi ? "IDI's default coverage applies when you save." : 'Showing your own coverage.'}
                  </p>
                  {!resetToIdi && (
                    <Button type="button" variant="secondary" size="sm" onClick={resetCoverageToIdi}>
                      Reset to IDI default
                    </Button>
                  )}
                </div>
              )}
              <ItemCoveragePanel item={form} onChange={setForm} />
            </>
          )}

          {/* PR-31 (307_ §4a): explain why Save is disabled inline (backup to the tooltip). */}
          {disabledReason && (
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
              {disabledReason}
            </p>
          )}
        </div>
      </Slideover>
    </>
  );
}

// DEF-162: exported for tests.
export const _DEFAULT_CATEGORY_FOR_TYPE = DEFAULT_CATEGORY_FOR_TYPE;
export const _blankItem = blankItem;

export default function QuoteItemsPage() {
  // IDI-509: Bulk Change writes the dealer's price row, so the super-admin
  // dealer workspace shows it again (IDI-650 D3).
  const workspace = useDealerWorkspace();
  // IDI-43 D1: "Upload prices" is the workspace's only; never a dealer's own page.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  // IDI-505 D4: the dealer's areas for an accessory's Areas (best-effort).
  const [areaRows, setAreaRows] = useState({ subAreas: [], customAreas: [] });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkField, setBulkField] = useState("itemCostPerUnit");
  const [bulkMode, setBulkMode] = useState("percent");
  const [bulkScope, setBulkScope] = useState("all");
  const [bulkValue, setBulkValue] = useState("");
  // IDI-759 D3: Apply is disabled until the save answers, across Cancel/reopen.
  const [bulkSaving, setBulkSaving] = useState(false);
  const bulkSavingRef = useRef(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [undoState, setUndoState] = useState(null); // for undo toast
  // R20-19: search + three filters. All compose (AND).
  const [query, setQuery] = useState('');
  const [filterRValue, setFilterRValue] = useState('__all__');
  const [filterCategory, setFilterCategory] = useState('__all__'); // IDI-75: category is now populated
  const [filterStatus, setFilterStatus] = useState('active'); // IDI-9: default to active for perf (7,534 rows)
  const [page, setPage] = useState(1); // IDI-9: flat-list pagination

  // PR-26 §3 (302_): derive the R-value filter facet from actual data. When
  // populated on <1% of items, the hardcoded R_VALUES list read as broken.
  // PR-27 (303_ §2): derive from the STATUS-filtered set so "Active" default
  // view doesn't offer dead R-value options (all 30 R-valued items are inactive).
  // PR-28 (304_ §2): read `i.status` (the row shape after buildCatalogRows) —
  // NOT `i.isActive` (the API shape that gets consumed there). Same expression
  // as the table filter below so the facet and the table classify identically.
  const availableRValues = useMemo(() => {
    const set = new Set();
    for (const i of items) {
      if (!i.rValue || i.rValue === '–') continue;
      if (filterStatus !== '__all__') {
        const status = i.status || 'active'; // matches line 597 exactly
        if (status !== filterStatus) continue;
      }
      set.add(String(i.rValue));
    }
    return Array.from(set).sort((a, b) => rSortKey(a) - rSortKey(b) || a.localeCompare(b));
  }, [items, filterStatus]);

  // IDI-75: category options for the filter, built from the resolved
  // categoryId on each row (populated once item_type_desc backfilled — see
  // buildCatalogRows above). Labels come from PRODUCT_CATEGORIES when the id
  // is one of the 20 canonical categories; otherwise title-case the raw
  // tenant-only slug (same rendering rule the quote-builder's tenant-only
  // category mechanism already uses — titleCaseCategoryId).
  const availableCategories = useMemo(() => {
    const seen = new Map(); // id -> label
    for (const i of items) {
      if (!i.categoryId) continue;
      if (filterStatus !== '__all__') {
        const status = i.status || 'active';
        if (status !== filterStatus) continue;
      }
      if (!seen.has(i.categoryId)) {
        const known = PRODUCT_CATEGORIES.find((c) => c.id === i.categoryId);
        seen.set(i.categoryId, known ? known.label : titleCaseCategoryId(i.categoryId));
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, filterStatus]);
  const toast = useToast();
  const confirm = useConfirm();

  // IDI-9: pristine pricebook base rows keyed by partNumber, captured at load
  // time. persist() diffs the rendered rows against this so we write ONLY the
  // tenant's edits to settings.quote_items, never the whole 7,534-row catalog.
  const baseByPart = useRef(new Map());
  // PR-32 (308_ u00a72): partNumbers present in the pricebook. Consumed by
  // handleDelete to distinguish pricebook rows from overlay-only custom rows.
  const pricebookSkus = useRef(new Set());

  // IDI-9: the pricebook (bidit-dev-pricebook, 7,534 active items) is the
  // authoritative catalog — NOT settings.quote_items, which is empty for most
  // tenants and only ever held a handful of hand-added rows. We read the
  // pricebook as the base row set and overlay per-tenant edits from
  // settings.quote_items, keyed by partNumber. The pricebook is a shared,
  // read-only base — edits and new items live in the overlay only; this page
  // never PUTs to the pricebook.
  //
  // The active rows carry no usable category metadata (see the IDI-9 grouping
  // audit), so we render a FLAT searchable/paginated table rather than the 20
  // seedCatalog categories.
  const load = useCallback(async () => {
    try {
      // IDI-507 (5): vendors / warehouses are the migrated lists (id + name).
      // settings.vendors / .warehouses are empty on every tenant measured, so
      // the selects offered nothing. A failed list leaves its select empty but
      // is logged, so it isn't mistaken for a dealer with no vendors.
      const [pricebook, res, vendorList, warehouseList] = await Promise.all([
        settingsAPI.getList('pricebook'),
        settingsAPI.getSettings(),
        settingsAPI.getList('vendors').catch((e) => { console.error('Item Catalog: vendor list failed to load', e); return []; }),
        settingsAPI.getList('warehouses').catch((e) => { console.error('Item Catalog: warehouse list failed to load', e); return []; }),
      ]);
      const s = res?.data || res || {};
      const overlays = Array.isArray(s.quote_items) ? s.quote_items : [];
      // Capture the pristine pricebook base (no overlays) so persist() can diff
      // against it and write only the tenant's edits.
      const pristine = buildCatalogRows(pricebook, []);
      // PR-32 (308_ §2): key on unique `id` (partNumber#idx), not partNumber.
      // The map name is retained (`baseByPart`) for grep-continuity, but its
      // key semantics have changed. See computeOverlayDelta above.
      baseByPart.current = new Map(pristine.map((r) => [r.id, r]));
      // IDI-338: the pricebook's own key is sku (partNumber is not unique).
      pricebookSkus.current = new Set(pristine.map((r) => String(r.sku || '')).filter(Boolean));
      setItems(buildCatalogRows(pricebook, overlays));
      setVendors(lookupOptions(vendorList));
      setWarehouses(lookupOptions(warehouseList));
    } catch (err) {
      setItems([]);
      setError('Failed to load the catalog — try refreshing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    const list = (key) => Promise.resolve().then(() => settingsAPI.getList(key))
      .then((rows) => (Array.isArray(rows) ? rows : [])).catch(() => []);
    Promise.all([list('sub_areas'), list('custom_areas')]).then(([subAreas, customAreas]) => {
      if (!cancelled) setAreaRows({ subAreas, customAreas });
    });
    return () => { cancelled = true; };
  }, []);

  // IDI-9: reset to page 1 whenever the search or filters change so a filtered
  // result set never lands the user on an out-of-range page.
  useEffect(() => { setPage(1); setSelectedIds([]); }, [query, filterRValue, filterCategory, filterStatus]);

  // Returns true when saved. IDI-507 (8): the error names what failed, and a
  // success toast fires only after the write resolved.
  //
  // IDI-441 (S1, D1): a save changes ONLY the rows this action touched.
  // `change` = { upsert: [rows this action changed], remove: [sku/id deleted] };
  // `next` is the new on-screen list. The stored settings.quote_items is read
  // fresh at save time and merged row by row (mergeOverlayRows), so a stored
  // row with no sku, a row that now matches the pricebook, or a row another
  // screen added since this page loaded (the builder's custom accessory) is
  // never dropped. IDI-9 still holds: only changed rows are written, never
  // the whole pricebook. PUT /v1/settings merges by top-level key, so no
  // other setting is touched.
  const persist = async (change, next, successMsg, failMsg = "Couldn't save the catalog") => {
    try {
      const current = await settingsAPI.getSettings();
      const s = current?.data || current || {};
      const stored = Array.isArray(s.quote_items) ? s.quote_items : [];
      const merged = mergeOverlayRows(stored, change.upsert || [], change.remove || [], baseByPart.current);
      await settingsAPI.updateSettings({ quote_items: merged });
      setItems(next);
      if (successMsg) toast.success(successMsg);
      return true;
    } catch (err) {
      toast.error(saveErrorMessage(failMsg, err));
      return false;
    }
  };

  const handleSave = async (item) => {
    // IDI-338 / IDI-428: a PRICEBOOK item's cost + coverage are the dealer's
    // own values -> write them to the dealer's pricebook override (read order
    // dealer -> IDI central -> none). This is what pricing, the Coverage
    // dropdown (IDI-173) and the Material List read. The settings overlay
    // below still records the row for the table's display fields.
    const sku = item.sku ? String(item.sku) : '';
    const label = item.name || sku || 'this item';
    // IDI-507 (1): the row as loaded, so only a coverage the user CHANGED is
    // sent as the dealer's own.
    const original = items.find((i) => i.id === item.id) || null;
    const isPricebookItem = Boolean(sku && pricebookSkus.current.has(sku));
    if (isPricebookItem) {
      try {
        await settingsAPI.savePricebookOverride(pricebookOverrideBody(item, original));
      } catch (err) {
        toast.error(saveErrorMessage(`Couldn't save the cost, coverage or status of "${label}"`, err));
        return;
      }
    }
    // PR-33 (309_ §3): sku as identity closed the fan-out that PR-30 warned
    // about — an edit now applies to exactly one row in-session AND on reload.
    // The PR-30 sibling-count warning is retired.
    const exists = Boolean(original);
    const next = exists ? items.map((i) => (i.id === item.id ? item : i)) : [...items, item];
    const ok = await persist(
      { upsert: [item] },
      next,
      exists ? 'Item updated' : 'Item added',
      isPricebookItem
        ? `Saved the cost and coverage of "${label}", but not its other fields`
        : `Couldn't save "${label}"`,
    );
    if (!ok) return; // keep the sheet open with the user's edits
    setEditing(null);
    // IDI-507 (4): the pricebook cache was dropped by the save; reload it in
    // the background so the coverage source (IDI's / the dealer's) is current.
    if (isPricebookItem) load();
  };

  // IDI-507 (7), RULED 10-14: an IDI (pricebook) item has NO Delete; it can
  // only be deactivated (Status in its sheet). A dealer's own custom item can
  // be deleted only if no quote, job or change order uses it; otherwise
  // Deactivate is offered with the reason. A delete used to drop only the
  // overlay, so a pricebook item "came back" on reload.
  const isIdiItem = (row) => Boolean(row && row.sku && pricebookSkus.current.has(String(row.sku)));
  const handleDelete = async (row) => {
    if (isIdiItem(row)) return; // no Delete is rendered; defensive
    const label = row.name || row.sku || 'this item';
    let usage;
    try {
      const res = await settingsAPI.getCatalogItemUsage(String(row.sku || row.id));
      usage = res?.data ?? res;
    } catch (err) {
      toast.error(saveErrorMessage(`Couldn't check whether "${label}" is used, so it wasn't deleted`, err));
      return;
    }
    if (usage?.in_use) {
      const already = (row.status || 'active') !== 'active';
      if (already) {
        toast.error(`"${label}" is used on a ${usage.found_in || 'quote'}, so it can't be deleted. It's already inactive.`);
        return;
      }
      const ok = await confirm({
        title: "This item can't be deleted",
        message: `"${label}" is used on a ${usage.found_in || 'quote'}, so it can't be deleted. Deactivate it instead? It stays on existing documents and is no longer offered on new quotes.`,
        confirmLabel: 'Deactivate',
      });
      if (!ok) return;
      const deactivated = { ...row, status: 'inactive' };
      await persist(
        { upsert: [deactivated] },
        items.map((i) => (i.id === row.id ? deactivated : i)),
        'Item deactivated',
        `Couldn't deactivate "${label}"`,
      );
      return;
    }
    const ok = await confirm({
      title: 'Delete item?',
      message: `Delete "${label}"? No quote, job or change order uses it.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    await persist(
      { remove: [String(row.sku || row.id)] },
      items.filter((i) => i.id !== row.id),
      'Item deleted', `Couldn't delete "${label}"`);
  };

  // R20-19: apply search + filters BEFORE grouping. Filters compose (AND).
  // The DEF-162 invariant compares against unfiltered `items` so a filter
  // miss doesn't fire the drop warning.
  // IDI-9: flat, searchable list — the pricebook has no usable category
  // metadata on active rows (see grouping audit), so we relax category grouping
  // and render one paginated table. Category filter removed accordingly.
  const filtersActive = !!query.trim()
    || filterRValue !== '__all__'
    || filterCategory !== '__all__'
    || filterStatus !== '__all__';

  const q = query.trim().toLowerCase();
  // IDI-508 (D3): filter the fixed default order, never the raw load order.
  const defaultOrdered = useMemo(() => [...items].sort(byNamePartSku), [items]);
  const filteredItems = defaultOrdered.filter((i) => {
    if (q) {
      // IDI-508 (D4): SKU is matched like part #.
      const hay = [
        i.name, i.salesDescription, i.partNumber, i.sku, i.description,
      ].filter(Boolean).map(String).map((s) => s.toLowerCase()).join(' | ');
      if (!hay.includes(q)) return false;
    }
    if (filterRValue !== '__all__' && String(i.rValue) !== filterRValue) return false;
    if (filterCategory !== '__all__' && i.categoryId !== filterCategory) return false;
    if (filterStatus !== '__all__') {
      const status = i.status || 'active'; // treat absent status as active
      if (status !== filterStatus) return false;
    }
    return true;
  });

  // IDI-509: Bulk Change. Cost targets the checked rows or every active row
  // matching the filters (D2), past the page; QTY only the checked rows (D5).
  const activeFiltered = filteredItems.filter((i) => (i.status || 'active') === 'active');
  // IDI-759 D1: every open starts with an empty value; Field and Mode stay.
  const openBulk = () => { setBulkScope(selectedIds.length > 0 ? 'checked' : 'all'); setBulkValue(''); setBulkOpen(true); };
  const bulkQty = bulkField === 'qtyOnHand';
  const checkedTargets = validateBulkSelection(selectedIds, items).targets || [];
  const bulkTargets = bulkQty || bulkScope === 'checked' ? checkedTargets : activeFiltered;
  const bulkNum = bulkValue.trim() === '' ? NaN : Number(bulkValue);
  const bulkPreview = Number.isFinite(bulkNum) ? bulkChangeRows(bulkTargets, bulkField, bulkMode, bulkNum) : null;
  const withCost = (c) => ({ ...c.row, itemCostPerUnit: c.after });
  // D1: pricebook items write the dealer's price row (one POST); custom items
  // their catalog row. Returns { saved, failed } or null when nothing saved.
  const saveCosts = async (changes, undo, failMsg) => {
    const pb = changes.filter((c) => isIdiItem(c.row));
    const custom = changes.filter((c) => !isIdiItem(c.row));
    const failed = new Set();
    try {
      if (pb.length) {
        const res = await settingsAPI.bulkChangePrices({ changes: pb.map((c) => ({ sku: String(c.row.sku), cost_cents: c.after })), undo });
        ((res?.data || res)?.failed || []).forEach((f) => failed.add(String(f.sku)));
      }
    } catch (err) { toast.error(saveErrorMessage(failMsg, err)); return null; }
    if (custom.length && !(await persist({ upsert: custom.map(withCost) }, items, null, failMsg))) {
      custom.forEach((c) => failed.add(String(c.row.sku)));
    }
    const saved = changes.filter((c) => !failed.has(String(c.row.sku)));
    const byId = new Map(saved.map((c) => [c.row.id, withCost(c)]));
    setItems((prev) => prev.map((i) => byId.get(i.id) || i));
    return { saved, failed: changes.filter((c) => failed.has(String(c.row.sku))) };
  };
  const applyBulk = async () => {
    if (bulkSavingRef.current) return;
    if (!bulkPreview || (bulkMode === 'sqft' && !(bulkNum > 0))) { toast.error(bulkMode === 'sqft' ? 'Enter a cost per sq ft above 0' : 'Enter a valid number'); return; }
    if (bulkTargets.length === 0) { toast.error(validateBulkSelection(selectedIds, items).error || 'No active items match the filters'); return; }
    const { changes } = bulkPreview;
    if (changes.length === 0) { toast.error('Nothing to change'); return; }
    if (!bulkQty && changes.length > 2000) { toast.error('Filter to 2,000 items or fewer.'); return; }
    let undo;
    bulkSavingRef.current = true;
    setBulkSaving(true);
    try {
      if (bulkQty) {
        const upsert = changes.map((c) => ({ ...c.row, qtyOnHand: c.after }));
        const byId = new Map(upsert.map((r) => [r.id, r]));
        if (!(await persist({ upsert }, items.map((i) => byId.get(i.id) || i), null, "Couldn't apply the bulk change"))) return;
        undo = { rows: changes.map((c) => c.row), message: `Bulk change applied to ${changes.length} item${changes.length !== 1 ? 's' : ''}.` };
      } else {
        const res = await saveCosts(changes, false, "Couldn't apply the bulk change");
        if (!res) return;
        undo = {
          costs: res.saved.map((c) => ({ row: withCost(c), after: Number(c.row.itemCostPerUnit) || 0 })),
          message: res.failed.length
            ? `Changed ${res.saved.length} of ${changes.length} items. ${res.failed.length} failed: ${res.failed.map((c) => c.row.sku).join(', ')}.`
            : `Bulk change applied to ${changes.length} item${changes.length !== 1 ? 's' : ''}.`,
        };
      }
    } finally {
      bulkSavingRef.current = false;
      setBulkSaving(false);
    }
    setBulkOpen(false);
    setSelectedIds([]);
    setUndoState(undo);
    setTimeout(() => setUndoState(null), 15000); // §1b: 15s for bulk (user needs to scroll + verify)
  };
  // D6: Undo restores only the saved rows' old costs, "not set" (0) included.
  const undoBulk = async () => {
    const u = undoState;
    setUndoState(null);
    if (u.rows) {
      const byId = new Map(u.rows.map((r) => [r.id, r]));
      await persist({ upsert: u.rows }, items.map((i) => byId.get(i.id) || i), "Bulk change undone");
    } else if (u.costs.length) {
      const res = await saveCosts(u.costs, true, "Couldn't undo the bulk change");
      if (!res) return; // saveCosts already said "Couldn't undo the bulk change"
      // IDI-759 D2: Undo's own words; failed rows stay at the changed price.
      if (res.failed.length) {
        toast.error(`Undid ${res.saved.length} of ${u.costs.length} items. ${res.failed.length} failed: ${res.failed.map((c) => c.row.sku).join(', ')}.`);
      } else {
        toast.success("Bulk change undone");
      }
    }
  };
  // IDI-9: paginate the flat list (7,534 active rows would otherwise render
  // 7,534 <tr> nodes and jank the page). Client-side pagination over the
  // already-filtered set.
  // IDI-508 (D2): sort every filtered row before paging; a sort returns to
  // page 1 and unchecks rows. Lives in memory only (D3: lasts until reload).
  const { sortedRows, getSortProps } = useTableSort(filteredItems, CATALOG_SORT_COLUMNS, {
    onSortChange: () => { setPage(1); setSelectedIds([]); },
  });
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageRows = sortedRows.slice(pageStart, pageStart + PAGE_SIZE);

  // IDI-94: multi-select helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === pageRows.length && pageRows.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pageRows.map(r => r.id));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
        <div className="h-32 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
      </div>
    );
  }

  return (
    <div className="px-6 space-y-6">
      <PageHeader
        title="Item Catalog"
        subtitle="Master defaults for line items. Every field is editable except the Part # of IDI's items (catalog key, read-only)."
        actions={
          <div className="flex items-center gap-2">
            {workspace && <Button variant="secondary" onClick={() => setUploadOpen(true)}>Upload prices</Button>}
            <DropdownMenu
              ariaLabel="New"
              trigger={
                <Button>
                  <PlusIcon className="h-4 w-4 mr-1" /> New
                  <ChevronDownIcon className="h-4 w-4 ml-1" />
                </Button>
              }
              items={[
                // IDI-505 D2: no type until the dealer picks one.
                { label: 'New Item', onClick: () => setEditing({ ...blankItem(), categoryId: '' }) },
                // IDI-505 D3: the same sheet, preset to Accessories.
                { label: 'New Accessory', onClick: () => setEditing({ ...blankItem(), categoryId: 'accessories', itemType: 'accessory' }) },
              ]}
            />
          </div>
        }
      />

      {workspace && (
        <PriceUploadSheet
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          // IDI-43 D4: the Item Catalog's name for that SKU, else the answer's.
          nameBySku={new Map(items.filter((i) => i.sku).map((i) => [String(i.sku), i.name]))}
          // IDI-43 D3: the client dropped the pricebook copy; reload in place.
          onSaved={load}
          toast={toast}
        />
      )}

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* IDI-75 (2026-08-24): category filter restored now that item_type_desc
          is backfilled on the pricebook base rows — see resolveCategoryIdFromItemTypeDesc
          in seedCatalog.js. Composes with search + R-value + status (AND). */}
      <div className="grid gap-3" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, description, or part #…"
          aria-label="Catalog search"
        />
        {availableCategories.length > 0 && (
          <SelectMenu
            width="full"
            value={filterCategory}
            onChange={setFilterCategory}
            options={[
              { value: '__all__', label: `All item types (${availableCategories.length})` },
              ...availableCategories,
            ]}
            aria-label="Filter by item type"
          />
        )}
        {availableRValues.length > 0 && (
          <SelectMenu
            width="full"
            value={filterRValue}
            onChange={setFilterRValue}
            options={[
              { value: '__all__', label: `All R-values (${availableRValues.length})` },
              ...availableRValues.map((r) => ({ value: r, label: r })),
            ]}
          />
        )}
        <SelectMenu
          width="full"
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: '__all__', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      </div>

      {/* R20-20: Bulk Change button */}
      <div className="flex items-center gap-2 mt-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={openBulk}
          disabled={filterStatus === 'inactive'}
          title={filterStatus === 'inactive' ? 'Bulk Change applies to active items only' : undefined}
        >
          Bulk Change
        </Button>
        {filtersActive && (
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            {/* PR-29 (305_ §4): the bulk operation targets ACTIVE items only
                (see the Apply handler below). On the Inactive view the count
                is legitimately 0 — name that in the hint so it doesn't read as
                a broken counter. */}
            {(() => {
              if (filterStatus === 'inactive') {
                return 'Bulk Change applies to active items only — switch the status filter to Active or All to use it here.';
              }
              if (selectedIds.length === 0) return `Will apply to all ${activeFiltered.length} matching items, or check items first`;
              return `Will apply to ${selectedIds.length} selected item${selectedIds.length === 1 ? '' : 's'} (active only)`;
            })()}
          </span>
        )}      </div>

      {filtersActive && (
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          Showing {filteredItems.length} of {items.length} item{items.length !== 1 ? 's' : ''}
          {' '}
          <button
            type="button"
            className="underline"
            style={{ color: 'var(--color-action-primary)' }}
            onClick={() => {
              setQuery('');
              setFilterCategory('__all__');
              setFilterRValue('__all__');
              setFilterStatus('__all__');
            }}
          >
            Clear filters
          </button>
        </p>
      )}

      {/* IDI-9: flat, paginated catalog table. No category grouping — the
          pricebook's active rows have no usable category metadata. */}
      {filteredItems.length === 0 ? (
        <p className="text-sm py-8 text-center" style={{ color: 'var(--color-text-muted)' }}>
          No catalog items match the current search or filters.
        </p>
      ) : (
        <>
          {/* IDI-94: bulk action bar */}
          <BulkActionBar
            count={selectedIds.length}
            onClear={() => setSelectedIds([])}
            actions={[
              { label: 'Bulk Change', onClick: openBulk },
            ]}
          />
          <div
            className="overflow-hidden"
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-10">
                    {/* IDI-94: raw <input> kept (not BulkRowCheckbox) because
                       BulkRowCheckbox does not forwardRef — indeterminate
                       requires a DOM ref. See BulkActionBar.js. */}
                    <input
                      type="checkbox"
                      ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < pageRows.length; }}
                      checked={selectedIds.length === pageRows.length && pageRows.length > 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all items on this page"
                      className="rounded border-gray-300 text-[color:var(--color-action-primary)] focus:ring-[color:var(--color-action-primary)]"
                      style={{ accentColor: 'var(--color-action-primary)' }}
                    />
                  </th>
                  <SortableTh {...getSortProps('name')}><th>Item Name</th></SortableTh>
                  <SortableTh {...getSortProps('itemType')}><th>Item Type</th></SortableTh>
                  <SortableTh {...getSortProps('rValue')}><th>R-Value</th></SortableTh>
                  <SortableTh {...getSortProps('cost')}><th style={{ textAlign: 'right' }}>Cost/Unit</th></SortableTh>
                  <SortableTh {...getSortProps('status')}><th>Status</th></SortableTh>
                  <SortableTh {...getSortProps('partNumber')}><th>Part #</th></SortableTh>
                  <SortableTh {...getSortProps('qtyOnHand')}><th style={{ textAlign: 'right' }}>QTY on Hand</th></SortableTh>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((i) => (
                  <tr key={i.id}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <BulkRowCheckbox
                        checked={selectedIds.includes(i.id)}
                        onChange={() => toggleSelect(i.id)}
                      />
                    </td>
                    <td className="font-medium">{i.name}</td>
                    <td>{itemTypeLabel(i)}</td>
                    <td>{rValueLabel(i)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {i.itemCostPerUnit ? `$${(Number(i.itemCostPerUnit) / 100).toFixed(2)}` : '—'}
                    </td>
                    <td>{statusLabel(i)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{i.partNumber || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{qtyLabel(i)}</td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditing(i)}
                          className="p-1 rounded hover:bg-[color:var(--neutral-100)]"
                          title="Edit"
                          aria-label={`Edit ${i.name}`}
                        >
                          <PencilSquareIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                        </button>
                        {!isIdiItem(i) && (
                          <button
                            onClick={() => handleDelete(i)}
                            className="p-1 rounded hover:bg-red-50"
                            title="Delete"
                            aria-label={`Delete ${i.name}`}
                          >
                            <TrashIcon className="h-4 w-4" style={{ color: 'var(--color-error)' }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* IDI-9: pagination controls */}
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredItems.length)} of {filteredItems.length}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => { setPage((p) => Math.max(1, p - 1)); setSelectedIds([]); }}
              >
                Previous
              </Button>
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                Page {safePage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); setSelectedIds([]); }}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <ItemEditorSheet
        item={editing || blankItem()}
        isNew={!!editing && !items.some((i) => i.id === editing.id)}
        isIdiItem={isIdiItem(editing)}
        areaRows={areaRows}
        vendors={vendors}
        warehouses={warehouses}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />

      {/* R20-20: Bulk Change modal */}
      <Slideover
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk Change"
        footer={
          <>
            <Button variant="secondary" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={applyBulk} disabled={bulkSaving}>Apply</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Field</label>
            <SelectMenu width="full" value={bulkField} onChange={(f) => { setBulkField(f); setBulkMode(f === 'qtyOnHand' ? 'add' : 'percent'); }} options={[
              { value: "itemCostPerUnit", label: "Cost" },
              { value: "qtyOnHand", label: "QTY on Hand" },
            ]} />
          </div>
          {!bulkQty && (
            <div>
              <label className="block text-sm font-medium mb-1">Items</label>
              <SelectMenu width="full" value={bulkScope} onChange={setBulkScope} options={[
                { value: "all", label: `All ${activeFiltered.length} items matching the filters` },
                { value: "checked", label: `Checked items (${checkedTargets.length})` },
              ]} />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Mode</label>
            <SelectMenu width="full" value={bulkMode} onChange={setBulkMode} options={bulkQty ? [
              { value: "add", label: "Add or subtract units" },
              { value: "set", label: "Set to" },
            ] : [
              { value: "percent", label: "By percent (%)" },
              { value: "dollar", label: "By dollars per unit ($)" },
              { value: "sqft", label: "Set cost per sq ft ($)" },
            ]} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Value (negatives allowed)</label>
            <Input id="bulk-value" type="number" step="any" placeholder="e.g., 5 or -5" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} />
          </div>
          {bulkPreview && (
            <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
              {`This changes ${bulkPreview.changes.length} items (active only).`}
              {bulkPreview.stays > 0 && ` ${bulkPreview.stays} stay as they are.`}
            </p>
          )}
        </div>
      </Slideover>

      {/* Undo snackbar — §1: the ONLY feedback for bulk operations */}
      {undoState && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-5 py-3 rounded-lg flex items-center gap-4 text-sm" >
          <span>{undoState.message}</span>
          <button
            type="button"
            className="font-medium"
            style={{ color: "var(--color-action-primary)" }}
            onClick={undoBulk}
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
}
